import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import {
  gradeOf,
  gradeOfScore,
  SPEED_METRIC_KEYS,
  SPEED_METRICS,
  type SpeedGrade,
  type SpeedMeasurement,
  speedIsFresh,
  type SpeedMetricKey,
} from 'shared';
import { formatDateTime, formatRelative } from '../format';
import { DISPLAY, tokens } from '../theme';
import { Note } from './Note';

/**
 * Bảng tốc độ thật — spec SC-18.
 *
 * VÌ SAO CÓ MÀN NÀY: mục kiểm tra `performance` trước đây chấm bằng thời gian máy
 * chủ trả byte đầu tiên rồi gắn nhãn "Performance". Đây là chỗ hiện con số thật, và
 * là chỗ duy nhất người dùng bấm được để lấy nó.
 */

const MAU: Record<SpeedGrade, { fg: string; bg: string; chu: string }> = {
  tot: { fg: tokens.okText, bg: tokens.okBg, chu: 'Tốt' },
  'trung-binh': { fg: tokens.amberText, bg: tokens.amberBg, chu: 'Cần cải thiện' },
  kem: { fg: tokens.roseText, bg: tokens.roseBg, chu: 'Kém' },
};

/** Đổi số thô sang cách viết người đọc hiểu ngay: giây cho thời gian, số thập phân cho CLS. */
function hienThi(key: SpeedMetricKey, value: number): string {
  if (key === 'clsX1000') return (value / 1000).toFixed(3);
  if (value >= 1000) return `${(value / 1000).toFixed(1)} giây`;
  return `${value} ms`;
}

function MetricCard({ khoa, value }: { khoa: SpeedMetricKey; value: number }) {
  const m = SPEED_METRICS[khoa];
  const mau = MAU[gradeOf(khoa, value)];

  return (
    <Box sx={{ bgcolor: '#fff', px: 2, py: 1.7 }}>
      <Stack direction="row" spacing={0.8} alignItems="baseline">
        <Typography sx={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 700, color: mau.fg }}>
          {hienThi(khoa, value)}
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: mau.fg,
            bgcolor: mau.bg,
            borderRadius: '5px',
            px: 0.7,
            py: 0.15,
          }}
        >
          {mau.chu}
        </Box>
      </Stack>
      <Typography sx={{ fontSize: 13, fontWeight: 500, mt: 0.4 }}>
        {m.label}{' '}
        <Box component="span" sx={{ color: tokens.faint, fontWeight: 400 }}>
          {m.viet}
        </Box>
      </Typography>
      {/* Câu giải thích là phần bắt buộc, không phải trang trí: người dùng của
          Copilot làm marketing, "CLS 0,31" không nói gì với họ. */}
      <Typography sx={{ fontSize: 12, color: tokens.muted, mt: 0.3, lineHeight: 1.45 }}>
        {m.giaiThich}
      </Typography>
    </Box>
  );
}

export function SpeedPanel({
  speed,
  onMeasure,
  dangDo,
  loi,
}: {
  speed: SpeedMeasurement | null;
  onMeasure: () => void;
  dangDo: boolean;
  loi: string | null;
}) {
  const conHan = speed ? speedIsFresh(speed.measuredAt) : false;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Tốc độ thật trên điện thoại</Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.muted }}>
            {speed
              ? `Google đo ${formatRelative(speed.measuredAt)} · ${formatDateTime(speed.measuredAt)}`
              : 'Chưa đo lần nào'}
          </Typography>
        </Box>
        <Button
          variant={speed ? 'outlined' : 'contained'}
          size="small"
          startIcon={dangDo ? <CircularProgress size={14} color="inherit" /> : <SpeedIcon />}
          disabled={dangDo}
          onClick={onMeasure}
          sx={{ flex: 'none' }}
        >
          {dangDo ? 'Google đang đo…' : speed ? 'Đo lại' : 'Đo tốc độ thật'}
        </Button>
      </Stack>

      {/* Im lặng 40 giây là người dùng bấm lại lần nữa (spec SC-18 §4 Y1). */}
      {dangDo && (
        <Box sx={{ mb: 1.5 }}>
          <Note>
            Google đang tải trang và chạy phép đo thật — việc này mất <b>10 đến 40 giây</b>. Cứ để
            màn hình đây, đừng bấm lại.
          </Note>
        </Box>
      )}

      {loi && (
        <Box sx={{ mb: 1.5 }}>
          <Note tone="bad">{loi}</Note>
        </Box>
      )}

      {!speed && !dangDo && !loi && (
        <Note>
          Mục <b>Thời gian máy chủ phản hồi</b> trong bảng kiểm tra chỉ đo được lúc máy chủ trả về
          byte đầu tiên — đó <b>chưa phải</b> tốc độ người dùng cảm nhận. Bấm nút trên để Google đo
          thật; sau đó lần quét kế tiếp sẽ chấm điểm theo số đo này.
        </Note>
      )}

      {speed && (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: '50%',
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                bgcolor: MAU[gradeOfScore(speed.perfScore)].bg,
                color: MAU[gradeOfScore(speed.perfScore)].fg,
                fontFamily: DISPLAY,
                fontSize: 22,
                fontWeight: 700,
              }}
            >
              {speed.perfScore}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                Điểm tốc độ của Google: {speed.perfScore}/100
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.muted }}>
                {conHan
                  ? 'Lần quét kế tiếp sẽ chấm mục tốc độ theo con số này.'
                  : 'Số đo đã quá 7 ngày nên không còn dùng để chấm điểm — đo lại để cập nhật.'}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '1px',
              bgcolor: tokens.line,
              border: `1px solid ${tokens.line}`,
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            {SPEED_METRIC_KEYS.map((k) => (
              <MetricCard key={k} khoa={k} value={speed[k]} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
